import { useEffect, useMemo, useState } from "react"
import { Search } from "lucide-react"
import { Navigate } from "react-router-dom"

import { RestrictedPaymentNotice } from "@/components/RestrictedPaymentNotice"
import { UsersTable } from "@/components/subject/UsersTable"
import {
  createUserTableRows,
  type UserTableRow,
} from "@/components/subject/userTableData"
import { Input } from "@/components/ui/input"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  deleteOtkUser,
  getOtkRoles,
  getOtkUsers,
  type OtkRole,
  type Role,
} from "@/lib/api/users"
import { useIsAdmin, useRolePermissions, useSession } from "@/store/session"

/**
 * subject-users.md's `/subject/users`, matching UD2 edit / UD3
 * destructive-warning dialogs, the row toolbar, search, and every
 * conditional branch it documents — seeded from the real
 * `GET /api/otk/users` / `GET /api/otk/roles` (otk-users.ts) rather than
 * local mock rows.
 *
 * There is no invite flow anymore: accounts are created automatically the
 * first time their KEP-holder logs in (see `kep-auth-plugin.ts`), so this
 * page only manages roles/access for already-existing users — UD3 (delete)
 * calls the real `POST /user/:id/delete` (`otk-users.ts`, via
 * `deleteOtkUser`) before updating local state, and UD2 (edit) replays the
 * real `/add-role`/`remove-role` calls.
 *
 * `restricted` has no real trigger in this app yet (the wireframe's version
 * is one of several `DemoScenariosPanel` toggles, which this app has no
 * equivalent of) — it's wired to a permanently-`false` constant so the
 * branch exists and is correct per spec without a way to reach it.
 */
export function SubjectUsers() {
  const canManageUsers = useRolePermissions().manageUsersRolesInvites
  const isAdmin = useIsAdmin()
  const restricted = false
  const [search, setSearch] = useState("")
  const [roleRows, setRoleRows] = useState<OtkRole[]>([])
  const [tableRows, setTableRows] = useState<UserTableRow[]>([])
  // Non-admins never fetch — the page redirects them away below — so their
  // initial `isLoading` is `false` rather than getting flipped inside the
  // effect (which would trigger an extra render).
  const [isLoading, setIsLoading] = useState(isAdmin)
  const [error, setError] = useState<string | null>(null)
  const subject = useSession((s) => s.subject)

  useEffect(() => {
    if (!isAdmin) return
    let cancelled = false
    Promise.all([getOtkUsers(), getOtkRoles()])
      .then(([users, roles]) => {
        if (cancelled) return
        const roleLabelById = Object.fromEntries(
          roles.map((role) => [role.id, role.name])
        )
        setRoleRows(roles)
        setTableRows(
          createUserTableRows(
            users,
            subject?.responsiblePersons ?? [],
            roleLabelById
          )
        )
      })
      .catch((err) => {
        if (cancelled) return
        const code = err?.response?.data?.error as string | undefined
        setError(
          code === "SUBJECT_NOT_CONNECTED"
            ? "Обліковий запис ще не пов'язаний із суб'єктом ОТК."
            : "Не вдалося завантажити список користувачів."
        )
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const removeRow = (id: string) => {
    setTableRows((current) => current.filter((row) => row.id !== id))
  }

  const deleteUser = async (id: string) => {
    await deleteOtkUser(id)
    removeRow(id)
  }

  const editUser = (id: string, roleId: Role) => {
    const roleLabel = roleRows.find((role) => role.id === roleId)?.name ?? roleId
    setTableRows((current) =>
      current.map((row) =>
        row.id === id ? { ...row, role: roleLabel, roles: [roleId] } : row
      )
    )
  }

  const filteredTableRows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase()
    if (!query) return tableRows
    return tableRows.filter((row) =>
      row.email.toLocaleLowerCase().includes(query)
    )
  }, [search, tableRows])

  // Managing users/roles is Адмін-only — every other role gets bounced back
  // to the dashboard rather than a read-only view of this page.
  if (!isAdmin) return <Navigate replace to="/" />

  return (
    <>
      {restricted && <RestrictedPaymentNotice />}
      <h1 className="mb-6 text-2xl font-semibold">Суб'єкт ОТК — Користувачі</h1>

      {isLoading && (
        <p className="text-sm text-muted-foreground">Завантаження…</p>
      )}
      {!isLoading && error && (
        <p className="text-sm text-destructive">{error}</p>
      )}

      {!isLoading && !error && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle>Користувачі</CardTitle>
              <CardDescription>
                Облікові записи АРМ. Кожен пов'язаний з відповідальною особою
                ЄДРТЗ автоматично, за РНОКПП КЕП, яким увійшли в систему.
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative w-full sm:max-w-md">
                <Search className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  aria-label="Пошук за e-mail або відповідальною особою"
                  className="pl-8"
                  onChange={(event) => {
                    setSearch(event.target.value)
                  }}
                  placeholder="Пошук за e-mail або відповідальною особою"
                  value={search}
                />
              </div>
            </div>
            {filteredTableRows.length === 0 ? (
              <div className="rounded-xl border border-dashed py-12 text-center">
                <p className="font-medium">Користувачів не знайдено</p>
                <p className="text-sm text-muted-foreground">
                  Змініть пошуковий запит.
                </p>
              </div>
            ) : (
              <UsersTable
                canManageUsers={canManageUsers}
                data={filteredTableRows}
                isAdmin={isAdmin}
                key={search}
                onDeleteUser={deleteUser}
                onEditUser={editUser}
                roleRows={roleRows}
              />
            )}
          </CardContent>
        </Card>
      )}
    </>
  )
}
